import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AddressFields, { emptyAddress } from '../components/AddressFields';
import OrderHeader from '../components/order/OrderHeader';
import { getJoinInfo, joinOrder } from '../api';
import { parseDay } from '../delivery';
import './Order.css';
import './Join.css';

// Where the curator's shared link lands. No account, no token - claiming a
// canvas only needs a name, an email and somewhere to post it.

// Remembering the claim means a refresh lands back on "you're in" rather
// than an empty form. It's only a convenience: the real guard is the
// backend rejecting a repeat email with a 409, which catches a different
// browser or a cleared store.
const claimKey = (publicId) => `mosaik-joined-${publicId}`;

function readClaim(publicId) {
  try {
    const stored = window.localStorage.getItem(claimKey(publicId));
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function rememberClaim(publicId, claim) {
  try {
    window.localStorage.setItem(claimKey(publicId), JSON.stringify(claim));
  } catch {
    // Storage being unavailable only costs us the nicety above.
  }
}

// Most painters are in the Netherlands, so the country starts filled in.
const DEFAULT_COUNTRY = { nl: 'Nederland', en: 'Netherlands' };

function Join() {
  const { t, i18n } = useTranslation();
  const { publicId } = useParams();
  const locale = i18n.resolvedLanguage === 'en' ? 'en-GB' : 'nl-NL';
  const day = (value) =>
    parseDay(value)?.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });

  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [numCanvases, setNumCanvases] = useState(1);
  const [address, setAddress] = useState(() => ({
    ...emptyAddress,
    country: DEFAULT_COUNTRY[i18n.resolvedLanguage] || DEFAULT_COUNTRY.nl,
  }));

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(() => readClaim(publicId));

  useEffect(() => {
    getJoinInfo(publicId).then(setInfo).catch((err) => setLoadError(err.message));
  }, [publicId]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const claim = await joinOrder(publicId, {
        first_name: firstName,
        last_name: lastName,
        email,
        num_canvases: numCanvases,
        address: info.address_needed ? address : null,
        // Their confirmation email goes out in whatever they're reading now.
        language: i18n.resolvedLanguage,
      });
      rememberClaim(publicId, claim);
      setDone(claim);
    } catch (err) {
      // 409 means this email already has a spot - they submitted twice, so
      // show them they're in rather than a failure.
      if (err.status === 409 && /already/i.test(err.message)) {
        const claim = { first_name: firstName, num_canvases: numCanvases, repeat: true };
        rememberClaim(publicId, claim);
        setDone(claim);
      } else {
        setError(err.message);
      }
    } finally {
      setSubmitting(false);
    }
  }

  let content;

  if (loadError) {
    content = (
      <>
        <h1 className="jn-title">{t('join.notFound')}</h1>
        <p className="jn-lede">{t('join.askSender')}</p>
      </>
    );
  } else if (!info) {
    content = <p className="jn-lede">{t('common.loading')}</p>;
  } else if (done) {
    const arrives = done.canvas_eta || info.canvas_eta;
    content = (
      <>
        <div className="ty-mark ty-mark--done jn-mark" aria-hidden="true">
          <svg viewBox="0 0 52 52">
            <path d="M14 27 l8 8 l17 -18" />
          </svg>
        </div>
        <h1 className="jn-title">
          {done.repeat ? t('join.done.alreadyTitle') : t('join.done.title', { name: done.first_name })}
        </h1>
        <p className="jn-lede">
          {done.repeat
            ? t('join.askSender')
            : arrives
            ? t('join.done.body', { date: day(arrives) })
            : t('join.done.bodyNoDate')}
        </p>
      </>
    );
  } else if (info.closed_code) {
    content = (
      <>
        <p className="jn-kicker">{info.event_name}</p>
        <h1 className="jn-title">{t(`join.closed.${info.closed_code}`)}</h1>
        <p className="jn-lede">{t('join.askSender')}</p>
      </>
    );
  } else {
    const max = Math.max(1, info.spots_left);
    content = (
      <>
        <p className="jn-kicker">{info.event_name}</p>
        <h1 className="jn-title">{t('join.title')}</h1>
        <p className="jn-lede">{t('join.lede', { event: info.event_name })}</p>

        <dl className="jn-facts">
          {info.deadline && (
            <div>
              <dt>{t('join.signUpUntil')}</dt>
              <dd>{day(info.deadline)}</dd>
            </div>
          )}
          {info.canvas_eta && (
            <div>
              <dt>{t('join.arrives')}</dt>
              <dd>{day(info.canvas_eta)}</dd>
            </div>
          )}
        </dl>

        <form className="jn-form" onSubmit={handleSubmit}>
          <div className="field-row">
            <label className="field">
              {t('common.firstName')}
              <input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
                required
              />
            </label>
            <label className="field">
              {t('common.surname')}
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
                required
              />
            </label>
          </div>
          <label className="field">
            {t('common.email')}
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </label>

          {info.address_needed && <AddressFields address={address} onChange={setAddress} />}

          {/* Painting together (a family, a couple) - one sign-up, more canvases. */}
          {max > 1 && (
            <div className="jn-count">
              <span>{t('join.canvases')}</span>
              <div className="jn-count-ctl">
                <button
                  type="button"
                  onClick={() => setNumCanvases((n) => Math.max(1, n - 1))}
                  disabled={numCanvases <= 1}
                  aria-label={t('join.less')}
                >
                  &minus;
                </button>
                <strong aria-live="polite">{numCanvases}</strong>
                <button
                  type="button"
                  onClick={() => setNumCanvases((n) => Math.min(max, n + 1))}
                  disabled={numCanvases >= max}
                  aria-label={t('join.more')}
                >
                  +
                </button>
              </div>
            </div>
          )}

          {error && (
            <p className="of-error" role="alert">
              {error}
            </p>
          )}

          <button type="submit" className="of-btn jn-submit" disabled={submitting}>
            {submitting ? t('join.saving') : t('join.button')} <span aria-hidden="true">&rarr;</span>
          </button>
          <p className="jn-note">{t('join.free')}</p>
        </form>
      </>
    );
  }

  return (
    <div className="atelier of">
      <OrderHeader home />
      <main className="jn">{content}</main>
    </div>
  );
}

export default Join;
